import i18n from "../language/i18n";
import { ONE_HOUR, retrieveDurationLabel } from "./DateUtilization";

// Achievement tier thresholds are owned by the backend (the source of truth that actually awards
// achievements). We fetch the catalog once (retrieveAchievementCatalog) and cache it here so the rule
// lookups below stay synchronous, instead of hardcoding a copy that drifts. Set via
// setAchievementRuleCatalog after the fetch resolves (see the achievement screens + LearnerApp init).
// Each entry is an AchievementDTO:
//   { coterieType, sourceType, trackingType, achievementRuleDTOList: [{ level, value, type }] }.
let achievementRuleCatalogList: any[] = [];

export const setAchievementRuleCatalog = (catalog: any[]) => {
  achievementRuleCatalogList = Array.isArray(catalog) ? catalog : [];
};

export const hasAchievementRuleCatalog = () => achievementRuleCatalogList.length > 0;

// ACTIVITY_TIME_SPENT thresholds arrive from the backend in milliseconds; every other family is a raw
// count. Convert to the unit the UI shows (hours, 1 decimal) so a threshold reads like its progress count.
export const toDisplayThreshold = (sourceType: string, rawValue: number) => {
  if (sourceType === "ACTIVITY_TIME_SPENT") {
    return Math.round((rawValue / ONE_HOUR) * 10) / 10;
  }
  return rawValue;
};

// A raw progress amount (current count or threshold) as the learner should read it: ACTIVITY_TIME_SPENT is
// milliseconds -> "1 saat 39 dk"; every other family is a plain count.
export const formatAchievementProgressValue = (sourceType: string, rawValue: number) =>
  sourceType === "ACTIVITY_TIME_SPENT" ? retrieveDurationLabel(rawValue) : `${rawValue ?? 0}`;

const retrieveSourceTypeOptionList = (coterieOnly: boolean) => {
  const currentSourceTypeOptionList = [
    {
      value: "BRANCH_ASSIGNMENT",
      text: i18n.t("BRANCH_ASSIGNMENT"),
    },
    {
      value: "PERSONALIZED_ASSIGNMENT",
      text: i18n.t("PERSONALIZED_ASSIGNMENT"),
    },
    {
      value: "CONTEST",
      text: i18n.t("CONTEST"),
    },
    {
      value: "ACTIVITY",
      text: i18n.t("ACTIVITY"),
    },
    {
      value: "ACTIVITY_CORRECT",
      text: i18n.t("ACTIVITY_CORRECT"),
    },
    {
      value: "ACTIVITY_REVIEW",
      text: i18n.t("ACTIVITY_REVIEW"),
    },
    {
      value: "ACTIVITY_TIME_SPENT",
      text: i18n.t("ACTIVITY_TIME_SPENT"),
    },
  ];
  if (!coterieOnly) {
    currentSourceTypeOptionList.push({
      value: "LOGIN",
      text: i18n.t("LOGIN"),
    });
  }
  return currentSourceTypeOptionList;
};

const retrieveTrackingTypeOptionList = () => {
  const currentTrackingTypeOptionList = [
    {
      value: "COUNT",
      text: i18n.t("COUNT"),
    },
  ];
  return currentTrackingTypeOptionList;
};

export const retrieveOtherAchievementDTOList = () => {
  const achievementList = [];
  const sourceTypeOptionList = retrieveSourceTypeOptionList(false);
  const filteredSourceTypeOptionList = sourceTypeOptionList.filter(
    (sourceTypeOption) => sourceTypeOption.value === "LOGIN"
  );
  for (let i = 1; i <= 5; i++) {
    for (const sourceTypeOption of filteredSourceTypeOptionList) {
      const trackingTypeOptionList = retrieveTrackingTypeOptionList();
      for (const trackingTypeOption of trackingTypeOptionList) {
        achievementList.push({
          achievementDTO: {
            coterieType: "MANAGEMENT",
            sourceType: sourceTypeOption.value,
            trackingType: trackingTypeOption.value,
          },
          level: i,
        });
      }
    }
  }
  // New cross-subject MANAGEMENT ("total") achievement families: activity streak (STREAK tracking
  // dimension), duel mastery (COUNT), and points milestone (POINT_TOTAL tracking dimension).
  // Each is a single family rather than per-coterie, so generate them explicitly here.
  const otherManagementAchievementFamilyList = [
    { sourceType: "ACTIVITY", trackingType: "STREAK" },
    { sourceType: "DUEL", trackingType: "COUNT" },
    { sourceType: "EARNED_POINTS", trackingType: "POINT_TOTAL" },
  ];
  for (let i = 1; i <= 5; i++) {
    for (const family of otherManagementAchievementFamilyList) {
      achievementList.push({
        achievementDTO: {
          coterieType: "MANAGEMENT",
          sourceType: family.sourceType,
          trackingType: family.trackingType,
        },
        level: i,
      });
    }
  }
  return achievementList;
};

export const retrieveAllEarnedAchievementDTOListByCoterieTypeList = (
  coterieTypeList: any
) => {
  const achievementList = [];
  for (const coterieType of coterieTypeList) {
    achievementList.push(...retrieveAllEarnedAchievementDTOListByCoterieType(coterieType));
  }
  return achievementList;
};

// Collapse the 5-per-family flat earned-achievement template list into one entry per
// (coterieType, sourceType, trackingType) family, preserving first-appearance order.
// MUST key by family (NOT chunk-of-5): retrieveOtherAchievementDTOList interleaves its extra
// MANAGEMENT families by level, so a positional assumption would corrupt them.
export const groupEarnedAchievementDTOListByFamily = (allEarnedAchievementDTOList: any[]) => {
  const orderedKeys: string[] = [];
  const byKey: Record<
    string,
    { familyKey: string; achievementDTO: any; levels: number[] }
  > = {};
  for (const earnedAchievement of allEarnedAchievementDTOList) {
    const { coterieType, sourceType, trackingType } = earnedAchievement.achievementDTO;
    const familyKey = `${coterieType}|${sourceType}|${trackingType}`;
    if (!byKey[familyKey]) {
      byKey[familyKey] = {
        familyKey,
        achievementDTO: { coterieType, sourceType, trackingType },
        levels: [],
      };
      orderedKeys.push(familyKey);
    }
    byKey[familyKey].levels.push(earnedAchievement.level);
  }
  orderedKeys.forEach((key) => byKey[key].levels.sort((a, b) => a - b));
  return orderedKeys.map((key) => byKey[key]);
};

// The learner's all-time count for an achievement family, derived from the bootstrap's
// `summedActionCounts` (per sourceType × coterieType, COUNT tracking only). Mirrors the backend
// NextAchievementCalculator countForAchievement exactly: a MANAGEMENT ("total") family sums across all coteries of its source type;
// a coterie family uses only the matching coterie; LOGIN counts distinct active days (`rowCount`)
// rather than the raw `total`. Returns 0 when nothing matches. Only meaningful for COUNT families.
export const computeFamilyActionCount = (
  summedActionCounts: any[],
  coterieType: string,
  sourceType: string
) => {
  if (!summedActionCounts || summedActionCounts.length === 0) return 0;
  const isLogin = sourceType === "LOGIN";
  const isManagementTotal = coterieType === "MANAGEMENT";
  let total = 0;
  for (const sum of summedActionCounts) {
    if (sum.sourceType !== sourceType) continue;
    if (!isManagementTotal && sum.coterieType !== coterieType) continue;
    const value = isLogin ? sum.rowCount : sum.total;
    if (typeof value === "number") total += value;
  }
  return total;
};

export const retrieveAllEarnedAchievementDTOListByCoterieType = (
  coterieType: string
) => {
  const sourceTypeOptionList = retrieveSourceTypeOptionList(true);
  const achievementList = [];
  for (let i = 1; i <= 5; i++) {
    for (const sourceTypeOption of sourceTypeOptionList) {
      const trackingTypeOptionList = retrieveTrackingTypeOptionList();
      for (const trackingTypeOption of trackingTypeOptionList) {
        achievementList.push({
          achievementDTO: {
            coterieType,
            sourceType: sourceTypeOption.value,
            trackingType: trackingTypeOption.value,
          },
          level: i,
        });
      }
    }
  }
  return achievementList.sort((a, b) => {
    if (a.achievementDTO.sourceType !== b.achievementDTO.sourceType) {
      return a.achievementDTO.sourceType.localeCompare(b.achievementDTO.sourceType);
    }
    return a.achievementDTO.trackingType.localeCompare(b.achievementDTO.trackingType);
  });
};

// The tier rules for a family, sourced from the backend catalog (NOT hardcoded — the previous hardcoded
// table drifted from the real award thresholds, so progress bars showed wrong targets while achievements
// stayed correctly locked). Returns [{ level, value }] sorted by level, or [] until the catalog is loaded —
// every consumer no-ops on an empty list. `value` is raw (ACTIVITY_TIME_SPENT in ms; convert via
// toDisplayThreshold).
export const retrieveAchievementRuleListByParams = (
  coterieType: string,
  sourceType: string,
  trackingType: string
) => {
  const foundAchievement = achievementRuleCatalogList.find(
    (achievement) =>
      achievement.coterieType === coterieType &&
      achievement.sourceType === sourceType &&
      achievement.trackingType === trackingType
  );
  if (!foundAchievement || !Array.isArray(foundAchievement.achievementRuleDTOList)) return [];
  return foundAchievement.achievementRuleDTOList
    .map((achievementRule: any) => ({ level: achievementRule.level, value: achievementRule.value }))
    .sort((a: any, b: any) => a.level - b.level);
};

export const retrieveAchievementRuleTextByParams = (
  coterieType: string,
  sourceType: string,
  trackingType: string,
  level: number
) => {
  const achievementRuleList = retrieveAchievementRuleListByParams(
    coterieType,
    sourceType,
    trackingType
  );
  const foundAchievementRule = achievementRuleList[level - 1];
  // Show the threshold in display units (ACTIVITY_TIME_SPENT comes from the backend in ms -> hours);
  // every other family passes through unchanged.
  const displayValue = foundAchievementRule
    ? toDisplayThreshold(sourceType, foundAchievementRule.value)
    : null;

  if (trackingType === "COUNT") {
    if (coterieType === "MANAGEMENT") {
      if (sourceType === "ACTIVITY") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_activity_text_1")}${
            displayValue
          }${i18n.t("total_count_activity_text_2")}`;
        } else {
          return i18n.t("none_count_activity_text");
        }
      } else if (sourceType === "ACTIVITY_CORRECT") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_activity_correct_text_1")}${
            displayValue
          }${i18n.t("total_count_activity_correct_text_2")}`;
        } else {
          return i18n.t("none_count_activity_correct_text");
        }
      } else if (sourceType === "ACTIVITY_REVIEW") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_activity_review_text_1")}${
            displayValue
          }${i18n.t("total_count_activity_review_text_2")}`;
        } else {
          return i18n.t("none_count_activity_review_text");
        }
      } else if (sourceType === "ACTIVITY_TIME_SPENT") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_activity_time_spent_text_1")}${
            displayValue
          }${i18n.t("total_count_activity_time_spent_text_2")}`;
        } else {
          return i18n.t("none_count_activity_time_spent_text");
        }
      } else if (sourceType === "BRANCH_ASSIGNMENT") {
        if (foundAchievementRule) {
          return `${i18n.t(
            "total_count_branch_assignment_text_1"
          )}${displayValue}${i18n.t(
            "total_count_branch_assignment_text_2"
          )}`;
        } else {
          return i18n.t("none_count_branch_assignment_text");
        }
      } else if (sourceType === "PERSONALIZED_ASSIGNMENT") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_personalized_assignment_text_1")}${
            displayValue
          }${i18n.t("total_count_personalized_assignment_text_2")}`;
        } else {
          return i18n.t("none_count_personalized_assignment_text");
        }
      } else if (sourceType === "CONTEST") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_contest_text_1")}${
            displayValue
          }${i18n.t("total_count_contest_text_2")}`;
        } else {
          return i18n.t("none_count_contest_text");
        }
      } else if (sourceType === "LOGIN") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_login_text_1")}${
            displayValue
          }${i18n.t("total_count_login_text_2")}`;
        } else {
          return i18n.t("none_count_login_text");
        }
      } else if (sourceType === "DUEL") {
        if (foundAchievementRule) {
          return `${i18n.t("total_count_duel_text_1")}${
            displayValue
          }${i18n.t("total_count_duel_text_2")}`;
        } else {
          return i18n.t("none_count_duel_text");
        }
      }
    } else {
      if (sourceType === "ACTIVITY") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_activity_text_1")}${
            displayValue
          }${i18n.t("coterie_count_activity_text_2")}`;
        } else {
          return i18n.t("none_count_activity_text");
        }
      } else if (sourceType === "ACTIVITY_CORRECT") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_activity_correct_text_1")}${
            displayValue
          }${i18n.t("coterie_count_activity_correct_text_2")}`;
        } else {
          return i18n.t("none_count_activity_correct_text");
        }
      } else if (sourceType === "ACTIVITY_REVIEW") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_activity_review_text_1")}${
            displayValue
          }${i18n.t("coterie_count_activity_review_text_2")}`;
        } else {
          return i18n.t("none_count_activity_review_text");
        }
      } else if (sourceType === "ACTIVITY_TIME_SPENT") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_activity_time_spent_text_1")}${
            displayValue
          }${i18n.t("coterie_count_activity_time_spent_text_2")}`;
        } else {
          return i18n.t("none_count_activity_time_spent_text");
        }
      } else if (sourceType === "BRANCH_ASSIGNMENT") {
        if (foundAchievementRule) {
          return `${i18n.t(
            "coterie_count_branch_assignment_text_1"
          )}${displayValue}${i18n.t(
            "coterie_count_branch_assignment_text_2"
          )}`;
        } else {
          return i18n.t("none_count_branch_assignment_text");
        }
      } else if (sourceType === "PERSONALIZED_ASSIGNMENT") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_personalized_assignment_text_1")}${
            displayValue
          }${i18n.t("coterie_count_personalized_assignment_text_2")}`;
        } else {
          return i18n.t("none_count_personalized_assignment_text");
        }
      } else if (sourceType === "CONTEST") {
        if (foundAchievementRule) {
          return `${i18n.t("coterie_count_contest_text_1")}${
            displayValue
          }${i18n.t("coterie_count_contest_text_2")}`;
        } else {
          return i18n.t("none_count_contest_text");
        }
      } else if (sourceType === "LOGIN") {
      }
    }
  }

  if (trackingType === "STREAK") {
    if (sourceType === "ACTIVITY") {
      if (foundAchievementRule) {
        return `${i18n.t("total_streak_activity_text_1")}${
          displayValue
        }${i18n.t("total_streak_activity_text_2")}`;
      } else {
        return i18n.t("none_streak_activity_text");
      }
    }
  }
  if (trackingType === "POINT_TOTAL") {
    if (sourceType === "EARNED_POINTS") {
      if (foundAchievementRule) {
        return `${i18n.t("total_point_total_earned_points_text_1")}${
          displayValue
        }${i18n.t("total_point_total_earned_points_text_2")}`;
      } else {
        return i18n.t("none_point_total_earned_points_text");
      }
    }
  }

  return null;
};

export const retrieveLeagueTypeOptionList = () => {
  return [
    {
      value: "CURIOUS_MIND",
      text: i18n.t("CURIOUS_MIND"),
    },
    {
      value: "OBSERVER",
      text: i18n.t("OBSERVER"),
    },
    {
      value: "THINKER",
      text: i18n.t("THINKER"),
    },
    {
      value: "LEARNER",
      text: i18n.t("LEARNER"),
    },
    {
      value: "ANALYST",
      text: i18n.t("ANALYST"),
    },
    {
      value: "INNOVATOR",
      text: i18n.t("INNOVATOR"),
    },
    {
      value: "EDUCATOR",
      text: i18n.t("EDUCATOR"),
    },
    {
      value: "VISIONARY",
      text: i18n.t("VISIONARY"),
    },
    {
      value: "MENTOR",
      text: i18n.t("MENTOR"),
    },
    {
      value: "LUMINARY",
      text: i18n.t("LUMINARY"),
    },
  ];
};

export const retrieveLeagueThresholds = () => {
  return {
    CURIOUS_MIND: { promotion: 0.5, stay: 0.5, demotion: 0.0 },
    OBSERVER: { promotion: 0.45, stay: 0.45, demotion: 0.1 },
    EXPLORER: { promotion: 0.4, stay: 0.45, demotion: 0.15 },
    THINKER: { promotion: 0.35, stay: 0.45, demotion: 0.2 },
    ANALYST: { promotion: 0.3, stay: 0.45, demotion: 0.25 },
    STRATEGIST: { promotion: 0.25, stay: 0.5, demotion: 0.25 },
    INNOVATOR: { promotion: 0.2, stay: 0.5, demotion: 0.3 },
    VISIONARY: { promotion: 0.15, stay: 0.5, demotion: 0.35 },
    MENTOR: { promotion: 0.1, stay: 0.5, demotion: 0.4 },
    LUMINARY: { promotion: 0.0, stay: 0.5, demotion: 0.5 },
  };
};

export const retrieveLeagueThresholdByLeagueType = (leagueType: string) => {
  const leagueThresholds: any = retrieveLeagueThresholds();
  return leagueThresholds[leagueType];
};

/**
 * P6: whether the learner-vs-learner leaderboard (and the learner's own rank) must stay hidden.
 * The backend is the authority — with the institution's switch off it ships empty individual lists and
 * the caller's own row with `rank` null — so that shape alone hides the board, even when the profile's
 * settings are stale or never carried the field. A learner with no points has no own row (null), which
 * keeps an ordinary empty leaderboard visible.
 */
export const isIndividualRankingHidden = (
  canShowIndividualRanking: boolean | undefined,
  rankingLists: any[][],
  selfRankings: any[],
) => {
  if (canShowIndividualRanking === false) return true;
  const allListsEmpty = rankingLists.every((list) => !list || list.length === 0);
  return (
    allListsEmpty &&
    selfRankings.some((self) => self && self.rank == null)
  );
};
